import * as React from "react";
import {DragDropContext, Droppable, Draggable} from '@hello-pangea/dnd';
import * as axios from "axios";
import {FaMapMarkerAlt} from "react-icons/fa";
import OrderDetailsModal from "./OrderDetailsModal";
import VehicleLocationModal from "./VehicleLocationModal";
import OrderCard from "./OrderCard";
import "./Orders.css";

const getItems = (count, offset = 0) =>
  Array.from({length: count}, (v, k) => k).map(k => ({
    id: `item-${k + offset}`,
    content: `item ${k + offset}`
  }));

// const baseUrl = 'http://localhost:8080/';
const baseUrl = 'https://api.appetito.ro/';
const userID = 38;
// A deliverer's break budget is 1h/day total, not 1h per break — it can be
// split across more than one (e.g. two 30-minute breaks). See
// Deliverer.break_minutes_used_today and DelivererBreak.budget_minutes,
// both computed server-side (models/delivererBreak.go).
const BREAK_DAILY_CAP_MINUTES = 60;
const ONE_HOUR_MS = 60 * 60 * 1000;

const remainingBreakMinutesToday = (deliverer) =>
  Math.max(0, BREAK_DAILY_CAP_MINUTES - (deliverer.break_minutes_used_today || 0));

// "HH:MM" -> a Date for that time on the same calendar day as referenceDate.
const timeToday = (hhmm, referenceDate) => {
  const [hours, minutes] = hhmm.split(':').map(Number);
  const date = new Date(referenceDate);
  date.setHours(hours, minutes, 0, 0);
  return date;
};

// A deliverer with no work_start configured is always considered in-window
// (unchanged from before this existed) — there's no hour to gate on.
const isWithinWorkWindow = (deliverer, now) => {
  if (!deliverer.work_start) {
    return true;
  }
  const start = timeToday(deliverer.work_start, now);
  const visibleFrom = new Date(start.getTime() - ONE_HOUR_MS);
  if (now < visibleFrom) {
    return false;
  }
  if (!deliverer.work_end) {
    return true;
  }
  const end = timeToday(deliverer.work_end, now);
  if (end <= start) {
    end.setDate(end.getDate() + 1); // shift spans midnight
  }
  return now <= end;
};

// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

/**
 * Moves an item from one list to another list.
 */
const move = (source, destination, droppableSource, droppableDestination) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  destClone.splice(droppableDestination.index, 0, removed);

  const result = {};
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;

  return result;
};

const formatMinutesRemaining = (msRemaining) => `${Math.max(0, Math.ceil(msRemaining / 60000))} min`;

const formatClock = (date) => `${`${date.getHours()}`.padStart(2, '0')}:${`${date.getMinutes()}`.padStart(2, '0')}`;

const BOARD_ZOOM_STEP = 0.1;
const BOARD_ZOOM_MIN = 0.5;
const BOARD_ZOOM_MAX = 1.5;

class Orders extends React.Component {

  state = {activeBreaks: {}, vehiclePositionByDelivererId: {}, boardZoom: 1};
  id2List = {};
  interval
  tickInterval

  componentDidMount = async () => {
    this.getDeliverers();
    this.interval = setInterval(this.getDeliverers, 5000);
    this.tickInterval = setInterval(() => this.setState({now: Date.now()}), 1000);
    const storedZoom = parseFloat(localStorage.getItem('boardZoom'));
    this.setState({
      darkMode: localStorage.getItem('darkMode') === 'true',
      boardZoom: Number.isFinite(storedZoom) ? storedZoom : 1,
    })
  };

  // CSS zoom (not transform: scale) — it reflows layout, so column widths,
  // the board's own scroll area, and everything else recompute correctly
  // instead of needing manual width/overflow compensation.
  adjustBoardZoom = (delta) => {
    this.setState((state) => {
      const boardZoom = Math.min(BOARD_ZOOM_MAX, Math.max(BOARD_ZOOM_MIN,
        Math.round((state.boardZoom + delta) * 100) / 100));
      localStorage.setItem('boardZoom', boardZoom);
      return {boardZoom};
    });
  };

  componentWillUnmount() {
    clearTimeout(this.dragErrorTimer);
    if (this.interval) {
      clearInterval(this.interval);
    }
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
    }
  }

  // Switching the sidebar's city dropdown re-renders this with new
  // cityIds — refetch right away rather than waiting up to 5s for the next
  // poll. Public.js passes this in; see CityContext for where it comes from.
  componentDidUpdate(prevProps) {
    if ((prevProps.cityIds || []).join(',') !== (this.props.cityIds || []).join(',')) {
      this.getDeliverers();
    }
  }

  // Comma-separated city ids for the ?cityIds= query param the backend
  // reads (see resolveCityIDs) — "" when not resolved yet falls back there
  // to the requesting admin's own city, same as not passing it at all.
  cityIdsParam = () => (this.props.cityIds || []).join(',');

  startBreak = async (delivererId) => {
    const token = localStorage.getItem('access_token');
    try {
      const response = await axios.default.post(`${baseUrl}v1/admin/deliverer/${delivererId}/break`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      this.setState({activeBreaks: {...this.state.activeBreaks, [delivererId]: response.data}});
    } catch (e) {
      // The button is already disabled once the daily cap is used up — this
      // only fires on a race (another tab, a poll landing mid-click).
      alert("Pauza zilnică de o oră a fost deja folosită.");
    }
  };

  stopBreak = async (delivererId) => {
    const delivererBreak = this.state.activeBreaks[delivererId];
    if (!delivererBreak) {
      return;
    }
    const token = localStorage.getItem('access_token');
    await axios.default.post(`${baseUrl}v1/admin/deliverer-break/${delivererBreak.id}/stop`, {}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    const activeBreaks = {...this.state.activeBreaks};
    delete activeBreaks[delivererId];
    this.setState({activeBreaks});
  };

  // Derived from the timestamp, not just the poll, so the UI doesn't lag a
  // stale "on break" state for up to 5s between polls. budget_minutes is
  // this specific break's allotted length — whatever was left of the daily
  // cap when it started, not a fixed hour — see BREAK_DAILY_CAP_MINUTES.
  breakRemainingMs = (delivererId) => {
    const delivererBreak = this.state.activeBreaks[delivererId];
    if (!delivererBreak) {
      return 0;
    }
    const startedAt = new Date(delivererBreak.started_at).getTime();
    const budgetMs = (delivererBreak.budget_minutes || 0) * 60000;
    const now = this.state.now || Date.now();
    return startedAt + budgetMs - now;
  };

  // A break record can exist for today but no longer be active — either
  // stopped early (ended_at set) or naturally lapsed past the hour.
  isOnBreak = (delivererId) => {
    const delivererBreak = this.state.activeBreaks[delivererId];
    if (!delivererBreak || delivererBreak.ended_at) {
      return false;
    }
    return this.breakRemainingMs(delivererId) > 0;
  };

  // Derived from the timestamp, not just the poll, same reasoning as
  // breakRemainingMs — ticks every second via this.state.now instead of
  // jumping every 5s poll.
  startsInMs = (deliverer) => {
    if (!deliverer.work_start) {
      return null;
    }
    const now = this.state.now || Date.now();
    return timeToday(deliverer.work_start, new Date(now)).getTime() - now;
  };

  hasNotStartedYet = (deliverer) => {
    const ms = this.startsInMs(deliverer);
    return ms !== null && ms > 0;
  };

  // "HH:MM-HH:MM" for a break that's already over today — end is either
  // when it was actually stopped, or when its budget ran out if it just
  // lapsed (that break's own budget_minutes, not always a full hour — a
  // second break that day might only have had 30 min left to give).
  formatBreakRange = (delivererBreak) => {
    const startTime = new Date(delivererBreak.started_at);
    const endTime = delivererBreak.ended_at
      ? new Date(delivererBreak.ended_at)
      : new Date(startTime.getTime() + (delivererBreak.budget_minutes || 0) * 60000);
    return `${formatClock(startTime)}-${formatClock(endTime)}`;
  };

  getDeliverers = async () => {
    const token = localStorage.getItem('access_token');
    const params = {cityIds: this.cityIdsParam()};
    const [activeResponse, allResponse, vehiclePositionsResponse] = await Promise.all([
      axios.default.get(`${baseUrl}v1/admin/active-deliverers`, {params, headers: {Authorization: `Bearer ${token}`}}),
      axios.default.get(`${baseUrl}v1/admin/all-deliverers`, {params, headers: {Authorization: `Bearer ${token}`}}),
      axios.default.get(`${baseUrl}v1/admin/vehicle-positions`, {params, headers: {Authorization: `Bearer ${token}`}}),
    ]);
    const orders = await this.getOrders();
    const duplicateNames = []
    for (let o1 = 0; o1 < orders.length; o1++) {
      for (let o2 = o1 + 1; o2 < orders.length; o2++) {
        if (orders[o1].user_name === orders[o2].user_name && orders[o1].user_name !== "Comanda Restaurant") {
          duplicateNames.push(orders[o1].user_name)
        }
      }
    }

    // A deliverer only shows up starting 1h before their work start, and
    // drops off past work end — except they never disappear while still
    // holding a non-delivered order, even if the schedule alone would hide
    // them entirely (off-shift, vacation, wrong day...). That's why the full
    // roster is fetched too: someone active-deliverers excludes outright
    // still needs to show up here if an order's stuck on them.
    const now = new Date();
    const delivererIdsWithActiveOrders = new Set(orders.filter((o) => o.deliverer_id).map((o) => o.deliverer_id));
    const timeGated = activeResponse.data.filter(
      (d) => isWithinWorkWindow(d, now) || delivererIdsWithActiveOrders.has(d.id)
    );
    const timeGatedIds = new Set(timeGated.map((d) => d.id));
    const rescued = allResponse.data.filter(
      (d) => !d.deleted && !timeGatedIds.has(d.id) && delivererIdsWithActiveOrders.has(d.id)
    );
    const deliverers = [...timeGated, ...rescued];

    let list = {
      0: orders.filter((order) => {
        order.id = `${order.order_id}`;
        return order.deliverer_id === 0;
      })
    };

    const activeBreaks = {};
    deliverers.map((deliverer, index) => {
      list[deliverer.id] = orders
        .filter((order) => {
          order.id = `${order.order_id}`;
          return order.deliverer_id === deliverer.id;
        })
        .sort((a, b) => a.position > b.position ? 1 : -1);
      if (deliverer.break) {
        activeBreaks[deliverer.id] = deliverer.break;
      }
    });

    // Rebuilt from scratch every poll (not just added to) — a deliverer can
    // now drop out of `deliverers` mid-day (work_end passed, no more active
    // orders), and a stale id2List entry pointing at a deliverer no longer
    // in state.deliverers crashes render() when it looks them up. Set
    // synchronously before setState, not in its callback, so the very next
    // render (using the new `deliverers` state) already sees the matching
    // id2List — not one render behind it.
    const id2List = {0: 0};
    deliverers.forEach((deliverer) => {
      id2List[deliverer.id] = deliverer.id;
    });
    this.id2List = id2List;

    // Location shown on the dashboard comes from TrackGPS (mounted in the
    // car), not deliverer.latitude/longitude (the deliverer's own phone GPS
    // from active-deliverers) — the car GPS is the reliable source here.
    // vehicle-positions only includes a deliverer_id when that car is
    // currently assigned to someone (see ListVehiclePositions on the
    // backend), so this map is naturally sparse.
    const vehiclePositionByDelivererId = {};
    (vehiclePositionsResponse.data || []).forEach((vehiclePosition) => {
      if (vehiclePosition.deliverer_id) {
        vehiclePositionByDelivererId[vehiclePosition.deliverer_id] = vehiclePosition;
      }
    });

    this.setState({deliverers, ...list, activeBreaks, duplicateNames, vehiclePositionByDelivererId});
  };

  getOrders = async () => {
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/orders`, {
      params: {cityIds: this.cityIdsParam()},
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  };

  getList = id => this.state[this.id2List[id]];

  // A move is shown on the board straight away, then saved (unassign →
  // assign → save the list's order). If any step fails — someone else took
  // the order first, a network/server error — say so and reload the board
  // from the server instead of leaving it looking assigned.
  saveMove = async (steps, orderId) => {
    try {
      for (const step of steps) {
        await step();
      }
    } catch (error) {
      const taken = error?.response?.status === 400;
      this.showDragError(taken
        ? `Comanda #${orderId} nu a putut fi asignată: probabil a fost deja preluată de alt livrator.`
        : `Comanda #${orderId} nu a putut fi mutată (eroare de rețea sau server). Încearcă din nou.`);
      this.getDeliverers();
    }
  };

  showDragError = (message) => {
    clearTimeout(this.dragErrorTimer);
    this.setState({dragError: message});
    this.dragErrorTimer = setTimeout(() => this.setState({dragError: null}), 8000);
  };

  onDragEnd = result => {
    const {source, destination, draggableId} = result;
    // dropped outside the list
    if (!destination) {
      return;
    }

    if (source.droppableId === destination.droppableId && destination.droppableId === "0") {
      return;
    }

    const positions = (list) => list.map((order, index) => ({order_id: order.order_id, position: index}));

    if (source.droppableId === destination.droppableId && destination.droppableId !== "0") {
      const reordered = reorder(this.getList(source.droppableId), source.index, destination.index);
      this.setState({[source.droppableId]: reordered});
      this.saveMove([
        () => axios.default.put(`${baseUrl}v1/delivery/orders/${source.droppableId}/reorder`, positions(reordered)),
      ], draggableId);
      return;
    }

    const moved = move(
      this.getList(source.droppableId),
      this.getList(destination.droppableId),
      source,
      destination
    );
    this.setState({...moved});

    const unpick = () => axios.default.post(`${baseUrl}v1/delivery/orders/${draggableId}/unpick`, {'user_id': `${userID}`});
    if (destination.droppableId === "0") {
      this.saveMove([unpick], draggableId);
      return;
    }
    this.saveMove([
      unpick,
      () => axios.default.post(`${baseUrl}v1/delivery/orders/${draggableId}/reserve`, {
        'user_id': `${destination.droppableId}`,
        'sendPush': true
      }),
      // The new order of the destination courier's list — saved (and the
      // "Comenzi rearanjate" push sent) for that courier, not the source.
      () => axios.default.put(`${baseUrl}v1/delivery/orders/${destination.droppableId}/reorder`, positions(moved[destination.droppableId])),
    ], draggableId);
  };

  render() {
    return (
      <>
      {!!this.state.dragError && (
        <div className="orders-drag-error" role="alert" onClick={() => this.setState({dragError: null})}>
          {this.state.dragError}
        </div>
      )}
      <div className="orders-board" style={{zoom: this.state.boardZoom}}>
        <DragDropContext onDragEnd={this.onDragEnd}>
          {Object.entries(this.id2List)
            .map(([key, value]) => {
              let deliverer = {};
              if (value === 0) {
                deliverer.first_name = 'Comenzi';
                deliverer.last_name = 'noi';
              } else {
                [deliverer] = this.state.deliverers.filter((deliverer) => {
                  return deliverer.id === value
                });
              }
              // Guards against a stale id2List entry outliving the
              // deliverer's presence in state.deliverers (e.g. a poll
              // landing mid-render) — skip the column that render rather
              // than crash on a lookup that came back empty.
              if (value !== 0 && !deliverer) {
                return null;
              }
              const delivererBreak = this.state.activeBreaks[value];
              const onBreak = value !== 0 && this.isOnBreak(value);
              const hasBreakToday = value !== 0 && !!delivererBreak && !onBreak;
              const notStarted = value !== 0 && !onBreak && this.hasNotStartedYet(deliverer);
              return {key, value, deliverer, delivererBreak, onBreak, hasBreakToday, notStarted};
            })
            .filter(Boolean)
            // "Comenzi noi" (id 0) always stays first; among actual
            // deliverers, available ones (started, not on break) come before
            // anyone on break or not started yet — ties keep ascending id.
            .sort((a, b) => {
              if (a.value === 0 || b.value === 0) {
                return a.value === 0 ? -1 : 1;
              }
              const aUnavailable = a.onBreak || a.notStarted;
              const bUnavailable = b.onBreak || b.notStarted;
              if (aUnavailable !== bUnavailable) {
                return aUnavailable ? 1 : -1;
              }
              return a.value - b.value;
            })
            .map(({key, value, deliverer, delivererBreak, onBreak, hasBreakToday, notStarted}) => {
            const isNew = value === 0;
            const orderCount = this.getList(key).length;
            const muted = onBreak || notStarted;
            const columnClassName = [
              'list-items-style', 'orders-column',
              isNew && 'orders-column--new',
              muted && 'orders-column--muted',
            ].filter(Boolean).join(' ');
            return <Droppable key={key} droppableId={key}>
              {(provided, snapshot) => (
                <div
                  className={`${columnClassName}${snapshot.isDraggingOver ? ' orders-column--drag-over' : ''}`}
                  ref={provided.innerRef}>
                  <div className="orders-column-header">
                    <div className="orders-column-header-left">
                      {isNew ? (
                        <>
                          <span className="orders-column-count-plain list-items-style-title">{orderCount}</span>
                          <span className="orders-column-name orders-column-name--new">Comenzi noi</span>
                        </>
                      ) : (
                        <>
                          <span className="orders-column-count-badge">{orderCount}</span>
                          <div className="orders-column-name-group">
                            <span className="orders-column-name list-items-style-title">
                              {deliverer.first_name} {deliverer.last_name}
                            </span>
                            {this.state.vehiclePositionByDelivererId[value] && (
                              <button
                                type="button"
                                className="orders-deliverer-location"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  this.setState({
                                    selectedVehiclePosition: this.state.vehiclePositionByDelivererId[value],
                                    selectedVehicleDelivererName: `${deliverer.first_name} ${deliverer.last_name}`,
                                  });
                                }}
                              >
                                <FaMapMarkerAlt size={11}/>
                                <span>Arată pe hartă</span>
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    {!isNew && (
                      onBreak ? (
                        <div className="orders-break-status">
                          <button type="button" className="orders-break-pill orders-break-pill--active" onClick={() => this.stopBreak(value)}>
                            În Pauză
                          </button>
                          <span className="orders-break-countdown">{formatMinutesRemaining(this.breakRemainingMs(value))}</span>
                        </div>
                      ) : notStarted ? (
                        <div className="orders-break-status">
                          <span className="orders-break-caption">Începe la {deliverer.work_start}</span>
                          <span className="orders-break-countdown">{formatMinutesRemaining(this.startsInMs(deliverer))}</span>
                        </div>
                      ) : remainingBreakMinutesToday(deliverer) > 0 ? (
                        <div className="orders-break-status">
                          <button type="button" className="orders-break-pill" onClick={() => this.startBreak(value)}>Pauză</button>
                          {hasBreakToday && (
                            <span className="orders-break-caption">{this.formatBreakRange(delivererBreak)}</span>
                          )}
                        </div>
                      ) : hasBreakToday ? (
                        // Daily cap used up (see BREAK_DAILY_CAP_MINUTES) — no
                        // button to take another break, just when the last one was.
                        <div className="orders-break-status">
                          <span className="orders-break-caption">{this.formatBreakRange(delivererBreak)}</span>
                        </div>
                      ) : null
                    )}
                  </div>
                  {this.getList(key).map((item, index) => (
                    <Draggable
                      key={item.id}
                      draggableId={item.id}
                      index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{flexShrink: 0, ...provided.draggableProps.style}}>
                          <OrderCard
                            order={item}
                            now={this.state.now}
                            duplicateName={this.state.duplicateNames.includes(item.user_name)}
                            onClick={() => this.setState({selectedOrder: item})}
                            dragHandleProps={provided.dragHandleProps}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          })}
        </DragDropContext>
      </div>
      <div className="orders-zoom-controls">
        <button
          type="button"
          className="orders-zoom-btn"
          onClick={() => this.adjustBoardZoom(BOARD_ZOOM_STEP)}
          disabled={this.state.boardZoom >= BOARD_ZOOM_MAX}
        >
          +
        </button>
        <button
          type="button"
          className="orders-zoom-btn"
          onClick={() => this.adjustBoardZoom(-BOARD_ZOOM_STEP)}
          disabled={this.state.boardZoom <= BOARD_ZOOM_MIN}
        >
          −
        </button>
      </div>
      <OrderDetailsModal
        order={this.state.selectedOrder}
        onClose={() => this.setState({selectedOrder: null})}
      />
      <VehicleLocationModal
        vehiclePosition={this.state.selectedVehiclePosition}
        delivererName={this.state.selectedVehicleDelivererName}
        onClose={() => this.setState({selectedVehiclePosition: null, selectedVehicleDelivererName: null})}
      />
      </>
    )
  }
}

export default Orders